import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { baseTranslations, loadTranslationsForRoute, getRouteKey } from '@/translations';
import { detectLanguageFromPath } from '@/lib/utils';

type Language = 'en' | 'pt';
type TranslationRecord = Record<string, string>;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  
  // Detect language from URL path first, then query param as fallback
  const [language, setLanguageState] = useState<Language>(() => {
    const pathLang = detectLanguageFromPath(window.location.pathname);
    if (pathLang === 'en') return 'en';
    
    // Fallback to query param for legacy URLs
    const params = new URLSearchParams(window.location.search);
    const langParam = params.get('lang');
    return (langParam === 'en' || langParam === 'pt') ? langParam : 'pt';
  });

  // Start with base translations (common + home) - loaded synchronously
  const [translations, setTranslations] = useState<{ en: TranslationRecord; pt: TranslationRecord }>(baseTranslations);
  const [loadedRoutes, setLoadedRoutes] = useState<Set<string>>(new Set());

  // Detect language from pathname changes (for /en/ routes)
  useEffect(() => {
    const pathLang = detectLanguageFromPath(location.pathname);
    if (pathLang !== language) {
      setLanguageState(pathLang);
      document.documentElement.lang = pathLang === 'pt' ? 'pt-MZ' : 'en';
    }
  }, [location.pathname, language]);

  // Load route-specific translations when pathname changes
  useEffect(() => {
    const pathname = location.pathname;
    const routeKey = getRouteKey(pathname);
    
    // Skip if already loaded or no route-specific translations needed
    if (!routeKey || loadedRoutes.has(routeKey)) return;

    loadTranslationsForRoute(pathname).then((routeTranslations) => {
      if (routeTranslations) {
        setTranslations(prev => ({
          en: { ...prev.en, ...routeTranslations.en },
          pt: { ...prev.pt, ...routeTranslations.pt },
        }));
        setLoadedRoutes(prev => new Set([...prev, routeKey]));
      }
    });
  }, [location.pathname, loadedRoutes]);

  const changeLanguage = (newLang: Language) => {
    setLanguageState(newLang);
    
    // Update HTML lang attribute
    document.documentElement.lang = newLang === 'pt' ? 'pt-MZ' : 'en';
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};